import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail, Lock, ArrowRight, ArrowLeft, CheckCircle2, Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { StatefulButton, type ButtonState } from "@/components/aceternity/stateful-button";
import { PageFrame } from "@/components/page-frame";
import { supabase } from "@/lib/supabase";
import { humaniseAuthError } from "@/lib/access";
import { useAuth } from "@/lib/auth-context";

type Tab = "social" | "login" | "signup";

export default function Login() {
  const [tab, setTab] = useState<Tab>("social");
  const loc = useLocation();
  const from = (loc.state as { from?: string })?.from || "/dashboard";

  return (
    <PageFrame surface="auth">
      <div className="min-h-screen relative">
        <div aria-hidden className="absolute inset-x-0 top-0 h-[500px] pointer-events-none"
          style={{ background: "radial-gradient(ellipse at top, rgba(201,169,106,0.18), transparent 60%)" }} />

        <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-fg-2 hover:text-fg transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="mx-auto max-w-6xl px-6 py-20 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">

          <aside className="hidden lg:flex flex-col justify-between gap-10 py-6">
            <BlurReveal>
              <div className="flex items-center gap-4">
                <img src="/logo.png" alt="" className="h-12 w-12 rounded-xl" />
                <div>
                  <div className="font-bold tracking-[0.15em] text-xl">ARABISE</div>
                  <div className="text-sm text-fg-2">GCSE Arabic, perfected.</div>
                </div>
              </div>
            </BlurReveal>

            <BlurReveal delay={0.15}>
              <div>
                <h1 className="text-4xl font-bold tracking-tight leading-tight">
                  Quizzes, sentences, and streaks —<br />
                  <span className="text-accent">all in one place.</span>
                </h1>
                <ul className="mt-8 space-y-4">
                  {PITCH.map(p => (
                    <li key={p} className="flex items-start gap-3 text-fg-2">
                      <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </BlurReveal>

            <BlurReveal delay={0.3}>
              <p className="text-xs text-fg-3">© 2026 Arabise</p>
            </BlurReveal>
          </aside>

          <main className="relative">
            <BlurReveal>
              <div className="rounded-3xl border border-line bg-bg-2/70 backdrop-blur-xl p-7 md:p-9 shadow-soft">
                <div className="lg:hidden flex items-center gap-3 mb-5">
                  <img src="/logo.png" alt="" className="h-9 w-9 rounded-lg" />
                  <span className="font-bold tracking-[0.15em]">ARABISE</span>
                </div>

                <h2 className="text-2xl font-bold tracking-tight">
                  {tab === "signup" ? "Create your account" : "Welcome back"}
                </h2>
                <p className="mt-1 text-sm text-fg-2">
                  {tab === "signup" ? "It takes a minute. Free to start." : "Sign in to keep learning."}
                </p>

                <div role="tablist" aria-label="Sign-in method" className="mt-6 flex border border-line rounded-full p-1 bg-bg">
                  {(["social","login","signup"] as Tab[]).map(t => (
                    <button
                      key={t}
                      role="tab"
                      aria-selected={tab === t}
                      onClick={() => setTab(t)}
                      className={
                        "flex-1 h-9 rounded-full text-sm font-medium transition-colors " +
                        (tab === t ? "bg-accent text-bg" : "text-fg-2 hover:text-fg")
                      }
                    >
                      {t === "social" ? "Social" : t === "login" ? "Login" : "Sign up"}
                    </button>
                  ))}
                </div>

                <div className="mt-6">
                  {tab === "social" && <SocialPanel />}
                  {tab === "login"  && <LoginPanel  redirectTo={from} />}
                  {tab === "signup" && <SignupPanel redirectTo={from} />}
                </div>

                <Separator className="my-6" />

                <div className="text-center text-xs text-fg-3">
                  By continuing you agree to our{" "}
                  <Link to="/terms" className="text-fg-2 hover:text-fg underline-offset-2 hover:underline">Terms</Link>
                  {" "}and{" "}
                  <Link to="/privacy" className="text-fg-2 hover:text-fg underline-offset-2 hover:underline">Privacy Policy</Link>.
                </div>
              </div>
            </BlurReveal>
          </main>
        </div>
      </div>
    </PageFrame>
  );
}

const PITCH = [
  "Daily streaks and the global leaderboard",
  "Sentence builder with live audio pronunciation",
  "Progress synced across every device",
  "Real AQA + Edexcel vocab. No mystery words."
];

/* ─────────────────────────── Social ─────────────────────────── */

function SocialPanel() {
  const [busy, setBusy] = useState<null | "google" | "azure">(null);
  const [err, setErr]   = useState("");

  async function oauth(provider: "google" | "azure") {
    setErr("");
    setBusy(provider);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin + window.location.pathname + "#/dashboard",
          queryParams: provider === "google" ? { prompt: "select_account" } : {}
        }
      });
      if (error) throw error;
      // Browser is being redirected.
    } catch (e) {
      setErr(humaniseAuthError(e));
      setBusy(null);
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-fg-2">Sign in with a provider. Quickest way in.</p>
      <button type="button" disabled={!!busy} onClick={() => oauth("google")}
        className="w-full h-11 inline-flex items-center justify-center gap-3 rounded-full border border-line bg-bg hover:bg-bg-3 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-sm font-medium">
        {busy === "google" ? <Loader2 className="w-4 h-4 animate-spin" /> : <GoogleIcon />} Continue with Google
      </button>
      <button type="button" disabled={!!busy} onClick={() => oauth("azure")}
        className="w-full h-11 inline-flex items-center justify-center gap-3 rounded-full border border-line bg-bg hover:bg-bg-3 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-sm font-medium">
        {busy === "azure" ? <Loader2 className="w-4 h-4 animate-spin" /> : <MicrosoftIcon />} Continue with Microsoft
      </button>
      {err && <ErrorBanner>{err}</ErrorBanner>}
    </div>
  );
}

/* ─────────────────────────── Login ─────────────────────────── */

function LoginPanel({ redirectTo }: { redirectTo: string }) {
  const nav = useNavigate();
  const { refreshAccess } = useAuth();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [state, setState]       = useState<ButtonState>("idle");
  const [err, setErr]           = useState("");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    setState("loading");
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password
      });
      if (error) throw error;
      setState("success");
      await refreshAccess();
      // Brief hold on success, then navigate.
      setTimeout(() => nav(redirectTo, { replace: true }), 700);
    } catch (e) {
      setErr(humaniseAuthError(e));
      setState("error");
      setTimeout(() => setState("idle"), 1600);
    }
  }

  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <Field label="Email" icon={<Mail className="w-4 h-4" />}>
        <Input type="email" autoComplete="username" inputMode="email" placeholder="you@example.com"
          className="pl-10" value={email} onChange={e => setEmail(e.target.value)} required />
      </Field>
      <Field label="Password" icon={<Lock className="w-4 h-4" />}>
        <Input type={showPw ? "text" : "password"} autoComplete="current-password" placeholder="••••••••"
          className="pl-10 pr-10" value={password} onChange={e => setPassword(e.target.value)} required />
        <EyeToggle on={showPw} set={setShowPw} />
      </Field>
      <div className="flex items-center justify-between text-xs">
        <span className="text-fg-3">30-day sessions, by default.</span>
        <Link to="/reset-password" className="text-fg-2 hover:text-fg">Forgot password?</Link>
      </div>
      {err && <ErrorBanner>{err}</ErrorBanner>}
      <StatefulButton
        type="submit"
        size="lg"
        state={state}
        className="w-full"
        idleLabel={<>Sign in <ArrowRight className="w-4 h-4" /></>}
        loadingLabel="Signing in…"
        successLabel="Welcome back"
        errorLabel="Try again"
      />
    </form>
  );
}

/* ─────────────────────────── Sign up ─────────────────────────── */

function SignupPanel({ redirectTo: _r }: { redirectTo: string }) {
  const { refreshAccess } = useAuth();
  const nav = useNavigate();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [showPw2, setShowPw2]   = useState(false);
  const [state, setState]       = useState<ButtonState>("idle");
  const [err, setErr]           = useState("");
  const [sentTo, setSentTo]     = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Pick a password with at least 8 characters."); setState("error"); setTimeout(()=>setState("idle"),1400); return; }
    if (password !== confirm) { setErr("Passwords don't match."); setState("error"); setTimeout(()=>setState("idle"),1400); return; }
    setState("loading");
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: window.location.origin + window.location.pathname + "#/onboarding"
        }
      });
      if (error) throw error;
      setState("success");
      if (data.session) {
        await refreshAccess();
        setTimeout(() => nav("/onboarding", { replace: true }), 700);
      } else {
        setTimeout(() => setSentTo(email.trim()), 700);
      }
    } catch (e) {
      setErr(humaniseAuthError(e));
      setState("error");
      setTimeout(()=>setState("idle"), 1600);
    }
  }

  if (sentTo) {
    return (
      <div className="text-center py-2">
        <CheckCircle2 className="w-10 h-10 text-accent mx-auto mb-3" />
        <h3 className="font-semibold">Confirm your email</h3>
        <p className="mt-2 text-sm text-fg-2">
          We've sent a confirmation link to <span className="text-fg">{sentTo}</span>.
          Click it to finish creating your account.
        </p>
      </div>
    );
  }

  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <Field label="Email" icon={<Mail className="w-4 h-4" />}>
        <Input type="email" autoComplete="username" inputMode="email" placeholder="you@example.com"
          className="pl-10" value={email} onChange={e => setEmail(e.target.value)} required />
      </Field>
      <Field label="Password" icon={<Lock className="w-4 h-4" />}>
        <Input type={showPw ? "text" : "password"} autoComplete="new-password" placeholder="At least 8 characters"
          className="pl-10 pr-10" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} />
        <EyeToggle on={showPw} set={setShowPw} />
      </Field>
      <Field label="Confirm password" icon={<Lock className="w-4 h-4" />}>
        <Input type={showPw2 ? "text" : "password"} autoComplete="new-password" placeholder="Repeat password"
          className="pl-10 pr-10" value={confirm} onChange={e => setConfirm(e.target.value)} required />
        <EyeToggle on={showPw2} set={setShowPw2} />
      </Field>
      {err && <ErrorBanner>{err}</ErrorBanner>}
      <StatefulButton
        type="submit"
        size="lg"
        state={state}
        className="w-full"
        idleLabel={<>Create account <ArrowRight className="w-4 h-4" /></>}
        loadingLabel="Creating…"
        successLabel="Account ready"
        errorLabel="Try again"
      />
    </form>
  );
}

/* ─────────────────────────── shared bits ─────────────────────────── */

function Field({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-fg-3 mb-2">{label}</span>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-3 pointer-events-none">{icon}</span>
        {children}
      </div>
    </label>
  );
}

function EyeToggle({ on, set }: { on: boolean; set: (v: boolean) => void }) {
  return (
    <button type="button" onClick={() => set(!on)}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-3 hover:text-fg"
      aria-label={on ? "Hide password" : "Show password"}>
      {on ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );
}

function ErrorBanner({ children }: { children: React.ReactNode }) {
  return (
    <div role="alert" className="flex items-start gap-2 rounded-lg border border-bad/40 bg-bad/10 px-3 py-2 text-sm text-bad">
      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 18 18" className="w-4 h-4" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 21 21" className="w-4 h-4" aria-hidden="true">
      <rect x="1"  y="1"  width="9" height="9" fill="#F25022"/>
      <rect x="11" y="1"  width="9" height="9" fill="#7FBA00"/>
      <rect x="1"  y="11" width="9" height="9" fill="#00A4EF"/>
      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
  );
}
